import {useMemo} from 'react';

import {getPersonalizedItemForCurrentCustomer} from 'brand/app/contentful/components/PersonalizedBlock/helpers';
import type {IPersonalizedItemProps} from 'brand/app/contentful/components/PersonalizedBlock/IPersonalizedBlock';

import useCustomerMgr from './useCustomerMgr';

export interface IHeaderRotationalBannerCmsData {
	headerCollection?: {
		items?: Array<{
			rotationalBanner?: {
				personalizedItemsCollection?: {
					items?: IPersonalizedItemProps[];
				};
			};
		}>;
	};
}

export type TParseRotationalBannerTexts = (
	item: IPersonalizedItemProps | null | undefined,
	...args: unknown[]
) => unknown;

export interface IHeaderRotationalBannerResult<TSlides = unknown> {
	rotationalBannerData: TSlides | null;
	livePreviewEntryId: string | null;
	backgroundColor?: string;
	textColor?: string;
}

/**
 * Resolves personalized rotational banner slides from processed header CMS data.
 * Parsing is delegated to the brand-specific parseTexts helper passed by the caller.
 */
const useHeaderRotationalBanner = <TSlides = unknown>(
	headerData: IHeaderRotationalBannerCmsData | undefined,
	parseTexts: TParseRotationalBannerTexts,
	...parseArgs: unknown[]
): IHeaderRotationalBannerResult<TSlides> => {
	const customerMgr = useCustomerMgr();
	const customer = customerMgr.getCurrentCustomer();

	return useMemo((): IHeaderRotationalBannerResult<TSlides> => {
		const items = headerData?.headerCollection?.items?.[0]?.rotationalBanner?.personalizedItemsCollection?.items;

		if (!items) {
			return {rotationalBannerData: null, livePreviewEntryId: null};
		}

		const personalizedItem: IPersonalizedItemProps = getPersonalizedItemForCurrentCustomer(items, customer);

		if (!personalizedItem) {
			return {rotationalBannerData: null, livePreviewEntryId: null};
		}

		return {
			rotationalBannerData: parseTexts(personalizedItem, ...parseArgs) as TSlides,
			backgroundColor: personalizedItem.backgroundColor,
			textColor: personalizedItem.textColor,
			livePreviewEntryId: personalizedItem?.item?.sys?.id ?? null,
		};
	}, [headerData, customer.getCustomerGroupsKey()]);
};

export default useHeaderRotationalBanner;
